import { createSelector, createSlice } from '@reduxjs/toolkit';
import { sliceNames } from '../util/constants';
import { capitalize, formatDeadlines, getCurrentEasternYear } from '../../util/functions';
import { selectAllowedToDownloadFromNonPublicS3 } from '../storage/slices';
import { sessionApi } from './api';
import { SESSION_KINDS, STATUSES } from '../../util/constants';
import { compareDesc, format, isAfter, isBefore, isValid } from 'date-fns'

const CURRENT_YEAR = getCurrentEasternYear()

const initialState = {
  active: {
    start: null,
    end: null,
    isActive: false,
    periodName: '',
    periodId: null,
    title: '',
    description: '',
  },
  dates: [],
  datesAndDeadlinesForTheYear: [],
  status: '',
  all: {
    selectOptions: []
  },
  drafts: {}
};

const formatSessionLabel = ({ label, year, period, start, formsCloseDate, formsOpenDate, end, isActive, pk, sk }) => {
  const openDate = start && new Date(start);
  const closeDate = end && new Date(end);
  
  if (!isValid(openDate) || !isValid(closeDate) || !year || !period) return null;
  
  const formattedOpen = format(openDate, 'MM/dd/yyyy');
  const formattedClose = format(closeDate, 'MM/dd/yyyy');
  const dateRange = `(${formattedOpen} - ${formattedClose})`;
  
  let optionLabel
  if (label) {
    optionLabel = `${label} ${dateRange}`;
  }
  
  optionLabel = `${year} - ${period} ${dateRange}`;
  return {
    label: optionLabel,
    pk,
    sk,
    year, 
    period,
    value: `${pk}::${sk}`,
    isActive,
    formsOpenDate, 
    formsCloseDate
  }
};

export const submissionPeriodSlice = createSlice({
  name: sliceNames.submissionPeriod,
  initialState,
  reducers: {
    setActiveSession: (state, action) => {
      state.active = action.payload
    },
    setDates: (state, action) => {
      state.datesAndDeadlinesForTheYear = action.payload
      state.dates = action.payload
    },
    setActiveSessionPaths: (state, action) => {
      state.paths = Array.isArray(action.payload) ? action.payload : []
    },
    resetPeriod: () => initialState,
    updateAllSessionsAction: (state, { payload }) => {
      updateAllSessions(state, payload)
    }
  },
   extraReducers: (builder) => {
      builder.addMatcher(
        sessionApi.endpoints.getSessionInfo.matchPending,
        (state) => {
          state.status = STATUSES.LOADING
        }
      );
      builder.addMatcher(
        sessionApi.endpoints.getSessionInfo.matchFulfilled,
        (state) => {
          state.status = STATUSES.SUCCESS
        }
      );
      builder.addMatcher(
        sessionApi.endpoints.getSessionInfo.matchRejected,
       (state) => {
          state.status = STATUSES.ERROR
        }
      );
      builder.addMatcher(
        sessionApi.endpoints.getAllSessions.matchFulfilled,
        (state, {payload}) => {
          updateAllSessions(state, payload)
        }
      );
    }
});

export const updateAllSessions = (state, sessions = []) => {
  if(!Array.isArray(sessions) || !sessions?.length) {
    return
  }

  const realSessionsMap = new Map()
  sessions.forEach(s => {
    realSessionsMap.set(`${s.year}-${s.period}`, s)
  })
  
  const allSessions = [
    ...sessions,
    ...state.dates.filter(d => 
      d.isDraft && !realSessionsMap.has(`${d.year}-${d.period}`)
    )
  ]
  
  state.dates = allSessions.sort((a, b) => {
    const yearA = parseInt(a.year)
    const yearB = parseInt(b.year)
    if (yearA !== yearB) return yearA - yearB
    
    const periodA = parseInt(a.period || 0)
    const periodB = parseInt(b.period || 0)
    if (periodA !== periodB) return periodA - periodB
    
    if (a.start && b.start) {
      return new Date(a.start) - new Date(b.start)
    }
    if (a.formsOpenDate && b.formsOpenDate) {
      return new Date(a.formsOpenDate) - new Date(b.formsOpenDate)
    }
    return 0
  })
  
  const options = sessions.map(session => {
    return updateSessionInState(state, session)
  }).filter(Boolean)

  if(options?.length) {
    state.all.selectOptions = options
  }
}

export const updateSessionInState = (state, session) => {
  const formatted = formatSessionLabel(session)
  if(formatted?.value && session?.s3Map) {
    const formTypes = Object.keys(session.s3Map)
    if(!state[formatted.value]) {
      state[formatted.value] = {}
    }
    state[formatted.value].formTypeOptions = formTypes.map(type => ({
      value: type.toUpperCase(),
      label: capitalize(type)
    }))
  }
  return formatted
}

export const { setPeriodDates, setPeriodInfo, resetPeriod, setDates, 
  setActiveSession, setActiveSessionPaths, updateAllSessionsAction } = submissionPeriodSlice.actions;
export default submissionPeriodSlice;

export function selectReportingPeriodTextInfo(state) {
  const { label = '', description = '' } = selectActiveReportingPeriod(state)|| {};

  return {
    title: label,
    description
  }
}

export function selectDates(state) {
  return state?.[sliceNames.submissionPeriod]?.dates || []
}

export function selectActiveReportingPeriod(state) {
  return state[sliceNames.submissionPeriod]?.active || {}
}

export function selectReportingPeriodStatus(state) {
  return state[sliceNames.submissionPeriod]?.status
}

export function selectReportingPeriodLoading(state) {
  return selectReportingPeriodStatus(state) === STATUSES.LOADING
}

export function selectActiveReportingPeriodPaths(state) {
  return state[sliceNames.submissionPeriod]?.paths || []
}

export function selectActiveReportingPeriodS3Map(state) {
  const map = selectActiveReportingPeriod(state)?.s3Map
  const validated = Object.entries(map || {})
    .filter(([key, path]) => path && path.startsWith("s3://") && path.endsWith(".json"))
  let s3Map = {}
  validated.forEach(([key, path]) => s3Map[key] = path)
  return s3Map
}

export function selectReportingPeriodIsOpen(state) {
  return Boolean(selectActiveReportingPeriod(state)?.isActive)
}

export const selectDeadlinesForYear = createSelector(
  [
    selectDates,
    (state, year) => year,
    (state, year, period) => period,
    (state, year, period, formatted) => formatted,
    (state, year, period, formatted, includeActivePeriod) => includeActivePeriod
  ],
  (dates = [], year, period, formatted, includeActivePeriod) => {  
    const filtered = dates.filter((date) => {
      if(includeActivePeriod && date.isActive) {
        return true;
      }
      if (`${date.year}` !== `${year}`) {
        return false;
      }
      if (!period) return true;
      return date?.period && `${date.period}` === `${period}`;
    });

    return formatted ? formatDeadlines(filtered) : filtered
  }
); 

export const selectCurrentYearDeadlines = createSelector(
  [
    (state) => state,
    () => CURRENT_YEAR
  ],
  (state, year) => {
    return selectDeadlinesForYear(state, year);
  }
);

export const selectFormattedDeadlines = (state, year, includeActivePeriod = false) => {
  return selectDeadlinesForYear(state, year, undefined, true, includeActivePeriod);
};

export const selectDatesAndDeadlines = createSelector(
  [(state) => state?.[sliceNames.submissionPeriod]?.datesAndDeadlinesForTheYear],
  (dates) => formatDeadlines(dates)
);

export const selectDeadlineForPeriod = (state, year, period, formatted = false) => {
  const deadlines = selectDeadlinesForYear(state, year, period, formatted);
  return deadlines?.[0] || null;
};

export const selectS3PathFromPeriod = (state, year, period, formType) => {
  const { s3Map = {} } = selectDeadlineForPeriod(state, year, period) || {};
  const path = (s3Map?.[formType] || "")
  return path
}

export function selectActiveReportingPeriodProperlyInitialized(state) {
  const validJsonMap = selectActiveReportingPeriodS3Map(state)
  const map = Object.keys(validJsonMap || {})?.length
  return map && selectAllowedToDownloadFromNonPublicS3(state)
}

export function selectSessionsDropdownOptions(state) {
  return state?.[sliceNames.submissionPeriod]?.all?.selectOptions || []
}

export const selectDefaultSession = (state) => {
  const sessions = selectSessionsDropdownOptions(state);
  if (!sessions?.length) {
    return {};
  }
  const activeSession = sessions.find((session) => session.isActive);
  if (activeSession) return activeSession;

  const now = new Date();
  const currentSession = sessions.find(
    (session) =>
      isAfter(now, new Date(session.formsOpenDate)) &&
      isBefore(now, new Date(session.formsCloseDate))
  );

  if (currentSession) return currentSession;
  const closedSessions = sessions
    .filter((session) => isBefore(new Date(session.formsCloseDate), now))
    .sort((a, b) =>
      compareDesc(new Date(a.formsCloseDate), new Date(b.formsCloseDate))
    );

  if (closedSessions?.length) {
    return closedSessions[0];
  }
  return sessions[sessions.length - 1];
};

export const selectFormTypeOptionsForSession = (state, session) => {
  return state[sliceNames.submissionPeriod]?.[session]?.formTypeOptions || []
}

export const selectRenderPaths = (state) => {
  return selectReportingPeriodIsOpen(state) && selectActiveReportingPeriodPaths(state)?.length
}

export const selectSessionsGroupedByYear = createSelector(
  [
    (state, type = SESSION_KINDS.FGC) => {
      return sessionApi.endpoints.getAllSessions.select({ type })(state)?.data
    }
  ],
  (allSessions = []) => {
    let currentYear = CURRENT_YEAR
    const priorYear = currentYear - 1
    const nextYear = currentYear + 1
    
    const groupedByYear = allSessions.reduce((acc, session) => {
      const year = parseInt(session.year)
      if (!acc[year]) {
        acc[year] = { year, sessions: [] }
      }
      acc[year].sessions.push(session)
      return acc
    }, {
        [priorYear]: { year: priorYear, sessions: [] },
        [currentYear]: { year: currentYear, sessions: [] },
        [nextYear]: { year: nextYear, sessions: [] }
    })
    
    return groupedByYear
    
  }
)